;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import { useState, useEffect, useMemo, useRef } from "react"
import updateErrorMessage from "../graphql/mutations/snackbarMessage/updateErrorMessage"
import debounce from "lodash/debounce"

export type AutocompleteQueryOptions = {
  componentRestrictions?: {
    country?: string | string[]
  }
  types?: string[]
  fields?: string[]
}

export type AutocompleteOption = {
  placePrediction?: {
    place?: string
    placeId?: string
    text?: { text?: string }
    structuredFormat?: {
      mainText?: {
        text?: string
        matches?: Array<{ startOffset?: number; endOffset?: number }>
      }
      secondaryText?: { text?: string }
    }
  }
  place_id?: string
  description?: string
  place?: string
  [key: string]: unknown
}

export type GeocodeResult = {
  lat?: number
  lng?: number
  latitude?: number
  longitude?: number
  placeId?: string
  place?: string
  formattedAddress?: string
  locationDescription?: string
  addressLine1?: string
  zipCode?: string
  city?: string
  state?: string
  stateAbbreviation?: string
  [key: string]: unknown
}

function normalizeAutocompleteDefault(
  value: AutocompleteOption | string | null | undefined,
): AutocompleteOption | null {
  if (value == null || value === "") {
    return null
  }
  if (typeof value === "string") {
    return { description: value }
  }
  return value
}

function getAutocompleteOptionDescription(
  option: AutocompleteOption | null,
): string {
  if (option == null) return ""
  return String(option.description ?? option.placePrediction?.text?.text ?? "")
}

export type UseGoogleAutocompleteArgs = {
  valueDefaultState: AutocompleteOption | string | null | undefined
  queryOptions?: AutocompleteQueryOptions
}

export type UseGoogleAutocompleteState = {
  value: AutocompleteOption | null
  inputValue: string
  options: AutocompleteOption[]
}

export type UseGoogleAutocompleteActions = {
  setValue: (value: AutocompleteOption | null) => void
  setInputValue: (value: string) => void
  setOptions: (options: AutocompleteOption[]) => void
  geoCodeOption: (option: AutocompleteOption | string) => Promise<GeocodeResult>
  fetchPlacePredictions: (input: string) => void
}

const useGoogleAutocomplete = ({
  valueDefaultState,
  queryOptions,
}: UseGoogleAutocompleteArgs): [
  UseGoogleAutocompleteState,
  UseGoogleAutocompleteActions,
] => {
  const [value, setValue] = useState<AutocompleteOption | null>(() =>
    normalizeAutocompleteDefault(valueDefaultState),
  )
  const [inputValue, setInputValue] = useState<string>(() =>
    getAutocompleteOptionDescription(
      normalizeAutocompleteDefault(valueDefaultState),
    ),
  )
  const [options, setOptions] = useState<AutocompleteOption[]>([])

  // Re-sync local state when the source value changes (adjust-state-in-render
  // pattern, see react.dev "You Might Not Need an Effect")
  const [prevValueDefaultState, setPrevValueDefaultState] =
    useState(valueDefaultState)
  if (valueDefaultState !== prevValueDefaultState) {
    setPrevValueDefaultState(valueDefaultState)
    const next = normalizeAutocompleteDefault(valueDefaultState)
    const nextDesc = getAutocompleteOptionDescription(next)
    setValue((prev) => {
      const prevDesc = getAutocompleteOptionDescription(prev)
      if (prevDesc === nextDesc) return prev
      return next
    })
    setInputValue(nextDesc)
  }

  const fetchRef = useRef<((input: string) => Promise<void>) | null>(null)

  const fetchPredictions = async (input: string) => {
    try {
      if (!input) {
        setOptions([])
        return
      }

      let url = `/edge/autocomplete?input=${encodeURIComponent(input)}`

      if (queryOptions) {
        if (queryOptions.componentRestrictions?.country) {
          const country = queryOptions.componentRestrictions.country
          if (Array.isArray(country)) {
            url += `&componentRestrictions.country=${country.join(",")}`
          } else {
            url += `&componentRestrictions.country=${country}`
          }
        }

        if (queryOptions.types && queryOptions.types.length > 0) {
          url += `&types=${queryOptions.types.join(",")}`
        }

        if (queryOptions.fields && queryOptions.fields.length > 0) {
          url += `&fields=${queryOptions.fields.join(",")}`
        }
      }

      const apiResponse = await fetch(url)
      const { data: { suggestions } = {} } = (await apiResponse.json()) as {
        data?: { suggestions?: AutocompleteOption[] }
      }

      setOptions(suggestions ?? [])
    } catch (error) {
      console.error("Failed to fetch predictions:", error)
      updateErrorMessage({
        message:
          "Failed to fetch autocomplete predictions. Please try again or contact support.",
        error,
        trackToSentry: true,
      })
    }
  }

  // Keep the ref pointing at the latest closure (with current queryOptions);
  // writing it in an effect keeps the write out of render.
  useEffect(() => {
    fetchRef.current = fetchPredictions
  })

  const debouncedFetch = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs -- debounce only defers the handler; fetchRef is read when the debounced call fires, not during render
      debounce((input: string) => {
        if (fetchRef.current) {
          fetchRef.current(input)
        }
      }, 300),
    [],
  )

  const fetchPlacePredictions = (input: string): void => {
    debouncedFetch(input)
  }

  const geoCodeOption = async (
    option: AutocompleteOption | string,
  ): Promise<GeocodeResult> => {
    let place: string | undefined
    let description: string | undefined

    if (typeof option === "string") {
      description = option
    } else if (option.placePrediction) {
      place = option.placePrediction.place
      description = option.placePrediction.text?.text ?? ""
    } else if (option.place_id) {
      place = option.place_id
      description = option.description
    } else {
      place = option.place
      description = option.description
    }

    try {
      const response = await fetch(`/edge/geocode-option`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ place, description }),
      })

      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string }
        const error = new Error(errorData.error ?? "Failed to geocode address")
        const isNotFound = response.status === 404
        if (!isNotFound) {
          console.error("Failed to getDetails of place", error)
        }
        updateErrorMessage({
          message:
            errorData.error ??
            "Failed to find that location. Please try a different search term.",
          trackToSentry: !isNotFound,
          error,
        })
        throw error
      }

      return (await response.json()) as GeocodeResult
    } catch (error) {
      const err = error as Error
      if (err.message && !err.message.includes("No location found")) {
        console.error("Failed to getDetails of place", err)
      }
      throw error
    }
  }

  useEffect(() => {
    return () => {
      debouncedFetch.cancel()
    }
  }, [debouncedFetch])

  return [
    { value, inputValue, options },
    { setValue, setInputValue, setOptions, geoCodeOption, fetchPlacePredictions },
  ]
}

export default useGoogleAutocomplete
